/* eslint-disable react/require-default-props */

import React from 'react';
import { Box, Button, Icon, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { type ImpactLevel, RoleImpact } from '../../../shared';
import { useInvertedLocalize } from '../../../utils/localization';

import Breadcrumbs from './Breadcrumbs/Breadcrumbs';
import invertedMessages from './messages';

import './RoleDetailsHeader.css';

interface RoleDetailsHeaderProps {
  title: string;
  subtitle?: string; // optional subtitle text
  icon?: React.ReactNode; // icon to display next to the title
  showBreadcrumbs?: boolean; // whether to show breadcrumbs nav
  impactLevel?: ImpactLevel; // impact level indicator
  className?: string;
}

/**
 * Header component for role details page containing title, subtitle, breadcrumbs, and impact indicator
 *
 * @param props - The component props
 * @returns JSX element containing the role details header
 */
function RoleDetailsHeader({
  title,
  subtitle,
  icon,
  showBreadcrumbs = false,
  impactLevel,
  className,
}: RoleDetailsHeaderProps) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <Box className={cx('Roles-RoleDetailsHeader', className)}>
      {showBreadcrumbs && (
        <Box className='Roles-RoleDetailsHeader-breadcrumbs'>
          <Breadcrumbs />
        </Box>
      )}

      <Box tag='header' className='Roles-RoleDetailsHeader-content'>
        <Box className='Roles-RoleDetailsHeader-topRow'>
          <Box className='Roles-RoleDetailsHeader-titleSection'>
            {icon && (
              <Box className='Roles-RoleDetailsHeader-icon'>
                {React.isValidElement(icon) && icon}
              </Box>
            )}

            <Box className='Roles-RoleDetailsHeader-titleRow'>
              <Typography
                kind='h1'
                color='--color-foreground-neutral-bold'
                className='Roles-RoleDetailsHeader-title'>
                {title}
              </Typography>
              {impactLevel && (
                <RoleImpact impactLevel={impactLevel} iconSize='small' />
              )}
            </Box>
          </Box>

          {/* Export button: MV2+ */}
          <Box className='Roles-RoleDetailsHeader-actions'>
            <Box className='Roles-RoleDetailsHeader-export'>
              <Button leftIcon={<Icon kind='Export' size='small' />}>
                {l('Export')}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Subtitle: aligned with left edge */}
        {subtitle && (
          <Typography
            kind='body2'
            color='--color-foreground-neutral-subtle'
            className='Roles-RoleDetailsHeader-subtitle'>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default RoleDetailsHeader;
